import { useMemo, useState } from "react";

import Avatar from "../../ui/avatar/Avatar";
import Button from "../../ui/buttons/Button";
import SearchInput from "../../ui/inputs/SearchInput";
import LoadingSpinner from "../../ui/spinner/LoadingSpinner";
import QuickContactForm from "../database/QuickContactForm";

import styles from "./UnassignedMailAssignPanel.module.css";

const MAX_VISIBLE_LEADS = 40;

const REASON_LABELS = {
  remitente: "remitente",
  copia: "CC",
  dominio: "dominio",
};

const normalize = (text) =>
  String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const getPersonRefName = (personRef) =>
  [personRef?.first_name, personRef?.last_name].filter(Boolean).join(" ") ||
  personRef?.email ||
  "";

const leadMatches = (lead, searchTokens) => {
  if (!searchTokens.length) return true;

  const haystack = normalize(
    [
      lead.lead_name,
      lead.company_name,
      lead.cliente_id,
      lead.account?.name,
      getPersonRefName(lead.person_ref),
      lead.person_ref?.email,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return searchTokens.every((token) => haystack.includes(token));
};

export const leadFromSuggestion = (suggestion) => ({
  id: suggestion.lead_id,
  lead_name: suggestion.lead_name,
  company_name: suggestion.company_name,
  company:
    suggestion.company_id != null
      ? { id: suggestion.company_id, name: suggestion.company_name ?? "" }
      : null,
  account: { id: suggestion.account_id, name: suggestion.account_name },
  cliente_id: null,
  status: null,
});

const describeReceiver = (lead) =>
  lead.person_ref?.email ||
  [getPersonRefName(lead.person_ref), lead.person_ref?.position].filter(Boolean).join(" · ") ||
  lead.company_name ||
  "Sin receptor";

const describeLead = (lead) =>
  [
    lead.cliente_id ? `#${lead.cliente_id}` : null,
    lead.status?.name || null,
    !lead.cliente_id && !lead.status?.name ? lead.company_name || null : null,
  ]
    .filter(Boolean)
    .join(" · ");

const UnassignedMailAssignPanel = ({
  mail,
  onContactCreated,
  leads = [],
  leadsLoading = false,
  leadsError = null,
  onLoadLeads,
  selectedLead = null,
  onSelectLead,
  onCreateLead,
  onAssign,
  assigning = false,
  assignError = null,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [createdByEmail, setCreatedByEmail] = useState({});
  const [excludedPersonIds, setExcludedPersonIds] = useState([]);
  const [quickFormEmail, setQuickFormEmail] = useState(null);

  const contactLayer = selectedLead?.account ?? mail?.contact_layer ?? null;

  const togglePicker = () => {
    if (!pickerOpen) onLoadLeads?.();
    setPickerOpen((previous) => !previous);
  };

  const personEntries = useMemo(
    () =>
      (mail?.persons ?? []).map((entry) => ({
        ...entry,
        person: entry.person || createdByEmail[entry.email] || null,
        wasCreated: Boolean(!entry.person && createdByEmail[entry.email]),
      })),
    [mail?.persons, createdByEmail],
  );

  const includedPersons = personEntries
    .filter((entry) => entry.person && !excludedPersonIds.includes(entry.person.id))
    .map((entry) => entry.person);
  const pendingContacts = personEntries.filter((entry) => !entry.person);

  const allSuggestions = mail?.suggested_leads ?? [];
  const suggestions = allSuggestions.filter(
    (suggestion, index) =>
      allSuggestions.findIndex((other) => other.lead_id === suggestion.lead_id) === index,
  );
  const suggestionReasons = [
    ...new Set(
      allSuggestions.map((suggestion) => REASON_LABELS[suggestion.reason] || suggestion.reason),
    ),
  ];

  const searchTokens = normalize(search).split(/\s+/).filter(Boolean);
  const visibleLeads = leads
    .filter((lead) => leadMatches(lead, searchTokens))
    .slice(0, MAX_VISIBLE_LEADS);

  const pickLead = (lead) => {
    onSelectLead?.(lead);
    setPickerOpen(false);
    setSearch("");
  };

  const togglePerson = (personId) => {
    setExcludedPersonIds((previous) =>
      previous.includes(personId)
        ? previous.filter((id) => id !== personId)
        : [...previous, personId],
    );
  };

  const handleContactCreated = (email, person) => {
    setCreatedByEmail((previous) => ({ ...previous, [email]: person }));
    setQuickFormEmail(null);
    onContactCreated?.(mail?.id, email, person);
  };

  const includedNames = includedPersons.map((person) => person.name || "").filter(Boolean);
  const pendingNames = pendingContacts.map((entry) => entry.name?.trim() || entry.email);

  const joinNames = (names) =>
    names.length <= 1
      ? names.join("")
      : `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;

  const summary = !selectedLead ? (
    "Elegí un lead para continuar"
  ) : (
    <>
      El hilo aparecerá en <b>{selectedLead.lead_name}</b>
      {includedNames.length > 0 && (
        <>
          {" "}
          con {joinNames(includedNames)} como{" "}
          {includedNames.length === 1 ? "persona relacionada" : "personas relacionadas"}
        </>
      )}
      .
      {pendingNames.length > 0 && (
        <>
          {" "}
          {joinNames(pendingNames)}{" "}
          {pendingNames.length === 1 ? "quedará" : "quedarán"} como{" "}
          <b>{pendingNames.length === 1 ? "contacto sin crear" : "contactos sin crear"}</b>{" "}
          (podés crearlos después desde la tarjeta).
        </>
      )}
    </>
  );

  return (
    <div className={styles.panel} data-testid="assign-panel">
      <section className={styles.step}>
        <header className={styles.stepHeader}>
          <span
            className={
              personEntries.length > 0 && pendingContacts.length === 0
                ? `${styles.stepNumber} ${styles.stepDone}`
                : styles.stepNumber
            }
          >
            {personEntries.length > 0 && pendingContacts.length === 0 ? "✓" : "1"}
          </span>
          <b>Personas del correo</b>
          <small>creá o relacioná los contactos antes de elegir el lead</small>
        </header>

        {personEntries.length === 0 && (
          <p className={styles.emptyPersons}>Este correo no tiene personas identificables.</p>
        )}

        {personEntries.map((entry) => {
          const displayName = entry.person?.name || entry.name?.trim() || entry.email;
          const included = entry.person && !excludedPersonIds.includes(entry.person.id);

          return (
            <div key={entry.email} className={styles.personRow}>
              <div className={styles.personMain}>
                <Avatar name={displayName} size={32} />
                <span className={styles.personInfo}>
                  <span className={styles.personName}>
                    {displayName}
                    <span
                      className={entry.person ? styles.personKnown : styles.personUnknown}
                      aria-label={entry.person ? "En la base" : "No está en la base"}
                    >
                      {entry.person ? "✓" : "?"}
                    </span>
                  </span>
                  <span className={styles.personMeta}>
                    {entry.email}
                    {" · "}
                    {entry.person
                      ? entry.person.position || entry.person.company_name || entry.role
                      : "no está en la base"}
                  </span>
                </span>
                {entry.person ? (
                  <button
                    type="button"
                    className={
                      included
                        ? `${styles.miniButton} ${styles.miniButtonActive}`
                        : styles.miniButton
                    }
                    onClick={() => togglePerson(entry.person.id)}
                    aria-pressed={included}
                  >
                    {included ? "✓ Se relaciona al lead" : "Relacionar al lead"}
                  </button>
                ) : quickFormEmail !== entry.email ? (
                  <button
                    type="button"
                    className={styles.miniButton}
                    onClick={() => setQuickFormEmail(entry.email)}
                    disabled={!contactLayer}
                    title={
                      contactLayer
                        ? undefined
                        : "La casilla no tiene una base de contactos asociada"
                    }
                  >
                    ＋ Crear contacto
                  </button>
                ) : null}
              </div>

              {!entry.person && quickFormEmail === entry.email && contactLayer && (
                <QuickContactForm
                  entry={entry}
                  account={contactLayer}
                  lead={selectedLead}
                  onCancel={() => setQuickFormEmail(null)}
                  onCreated={(person) => handleContactCreated(entry.email, person)}
                />
              )}
            </div>
          );
        })}
      </section>

      <section className={styles.step}>
        <header className={styles.stepHeader}>
          <span className={selectedLead ? `${styles.stepNumber} ${styles.stepDone}` : styles.stepNumber}>
            {selectedLead ? "✓" : "2"}
          </span>
          <b>Lead</b>
          <small>¿A qué tarjeta va este correo?</small>
        </header>

        {selectedLead ? (
          <div className={styles.selectedLead}>
            {selectedLead.account?.name && (
              <span className={styles.accountTag}>{selectedLead.account.name}</span>
            )}
            <span className={styles.leadInfo}>
              <span className={styles.leadName}>{selectedLead.lead_name}</span>
              <span className={styles.leadMeta}>{describeLead(selectedLead)}</span>
            </span>
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => onSelectLead?.(null)}
              aria-label="Quitar lead seleccionado"
            >
              ×
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={styles.leadPickerTrigger}
              onClick={togglePicker}
              aria-expanded={pickerOpen}
            >
              Buscar lead por empresa o contacto...
            </button>

            {pickerOpen && (
              <div className={styles.dropdown}>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Empresa, contacto o cuenta..."
                  height={36}
                  width="100%"
                />
                {leadsError ? (
                  <div className={styles.dropdownState} role="alert">
                    <span>{leadsError}</span>
                    <button type="button" className={styles.miniButton} onClick={onLoadLeads}>
                      Reintentar
                    </button>
                  </div>
                ) : leadsLoading ? (
                  <div className={styles.dropdownState} role="status">
                    <LoadingSpinner size={18} thickness={2} />
                    <span>Cargando tus leads...</span>
                  </div>
                ) : visibleLeads.length === 0 ? (
                  <div className={styles.dropdownState}>
                    {searchTokens.length ? "Sin resultados." : "No tenés leads para vincular."}
                  </div>
                ) : (
                  <div className={styles.options} role="listbox" aria-label="Leads">
                    {visibleLeads.map((lead) => (
                      <button
                        key={lead.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className={styles.option}
                        onClick={() => pickLead(lead)}
                      >
                        <span className={styles.optionHeadline}>
                          {lead.account?.name && (
                            <span className={styles.accountTag}>{lead.account.name}</span>
                          )}
                          <span className={styles.leadName}>{lead.lead_name}</span>
                        </span>
                        <span className={styles.leadMeta}>{describeReceiver(lead)}</span>
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className={styles.createLeadButton}
                  onClick={() => {
                    setPickerOpen(false);
                    onCreateLead?.();
                  }}
                >
                  ＋ Crear lead nuevo con este correo
                </button>
              </div>
            )}

            {suggestions.length > 0 && (
              <div className={styles.suggestions}>
                <span>Sugerido por {joinNames(suggestionReasons)}:</span>
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.lead_id}
                    type="button"
                    className={styles.suggestionPill}
                    onClick={() => pickLead(leadFromSuggestion(suggestion))}
                    title={suggestion.account_name || ""}
                  >
                    {suggestion.lead_name}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {assignError && (
        <p className={styles.error} role="alert">
          {assignError}
        </p>
      )}

      <Button
        variant="special"
        tone="blue"
        fullWidth
        height={48}
        disabled={!selectedLead || assigning}
        loading={assigning}
        onClick={() =>
          onAssign?.({
            lead: selectedLead,
            personIds: includedPersons.map((person) => person.id),
            personNames: includedNames,
          })
        }
      >
        Vincular al lead
      </Button>

      <p className={styles.summary}>{summary}</p>
    </div>
  );
};

export default UnassignedMailAssignPanel;
